import React, { useMemo, useState } from 'react';
import { chain, isEmpty, map } from 'lodash';
import { Button, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import styles from './VendorInitializer.module.css';
import TextInput from '../ReusableComponents/TextInput/TextInput';
import Role, { hasPermissionTo, Actions } from '../../Lib/Constants/roles.ts';
import validateEmail from '../../Lib/Utils/Helpers/validate-email';
import VendorSelector from '../ReusableComponents/VendorSelector/VendorSelector';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';

const setupForm = (currentUser = {}) => ({
  company_name: '',
  email: '',
  reason: '',
  company_type: 'N/A',
  requesting_party: 'N/A',
  vendor_type: Role.INFLUENCER,
  originated_by: currentUser.email,
  requesting_party_id: currentUser.id,
  vendor_id: null,
});

function InfluencerInitializer(props) {
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(setupForm(props.currentUser));
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState(null);

  const validations = {
    company_name: () => !isEmpty(form.company_name),
    email: () => !isEmpty(form.email) && validateEmail(form.email),
    reason: () => !isEmpty(form.reason),
    vendor_id: () => true,
  };

  function validateForm() {
    map(validations, (validation, name) => {
      if (!validation()) {
        setFormErrors((errors) => ({
          ...errors,
          [name]: true,
        }));
      }
    });

    return chain(validations)
      .map((f) => f())
      .every()
      .value();
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);

    props
      .sendForm(form)
      .then(() => {
        setSaved(true);
        setError(null);
      })
      .catch((err) => {
        setError(err.message || err);
      })
      .finally(() => setSaving(false));
  };

  const onChange = (e) => {
    if (e.persist) e.persist();
    setFormErrors((errors) => ({ ...errors, [e.target.name]: null }));
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const onBlur = (e) => {
    e.persist();

    if (!validations[e.target.name]()) {
      setFormErrors((errors) => ({
        ...errors,
        [e.target.name]: true,
      }));
    }
  };

  const canCreateInfluencer = useMemo(
    () =>
      hasPermissionTo(
        'social-media-console/influencer-init',
        props.currentUser.role,
        Actions.CREATE,
        'INFLUENCER',
      ),
    [props.currentUser],
  );

  return (
    <Page.Container withoutTwp>
      <Header.Major
        icon="camera"
        title="Influencers"
        subTitle="Initializer Form"
        onCloseLink="/social-media-console"
      />
      <Page.Content>
        <form onSubmit={handleSubmit}>
          <div className={styles.row}>
            <div className={styles.halfRow}>
              <TextInput
                fullWidth
                label="Company Name"
                variant="outlined"
                name="company_name"
                value={form.company_name}
                onChange={onChange}
                onBlur={onBlur}
                disabled={saving || saved}
                formHelperText={
                  formErrors.company_name &&
                  'Please fill out a valid company name'
                }
              />
            </div>
            <div className={styles.halfRow}>
              <TextInput
                fullWidth
                label="Email"
                variant="outlined"
                name="email"
                autoComplete="no"
                value={form.email}
                onChange={onChange}
                onBlur={onBlur}
                disabled={saving || saved}
                formHelperText={
                  formErrors.email && 'Please fill out a valid email'
                }
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.halfRow}>
              <TextInput
                fullWidth
                label="Reason For Adding Vendor"
                variant="outlined"
                name="reason"
                autoComplete="no"
                value={form.reason}
                onChange={onChange}
                onBlur={onBlur}
                disabled={saving || saved}
                formHelperText={
                  formErrors.reason && 'Please fill out a valid reason'
                }
              />
            </div>
            <div className={styles.halfRow}>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                Select a default influencer (optional)
              </Typography>
              <VendorSelector
                approvedOnly
                placeholder="Select Influencer"
                onChange={({ value }) =>
                  onChange({ target: { name: 'vendor_id', value } })
                }
                searchVendors={(search) => {
                  console.log(search);
                  props.getInfluencers({ search });
                }}
                vendors={map(props.influencers, 'vendor')}
              />
            </div>
          </div>
          <div className={styles.submitButtonContainer}>
            <Button
              className={styles.submitButton}
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={saving || saved || !canCreateInfluencer}
            >
              {saving ? 'LOADING' : 'SUBMIT'}
            </Button>
            {saved && (
              <div
                className={styles.submissionMessage}
                style={{ color: 'green', fontSize: '20px' }}
              >
                The token has been created and sent.
              </div>
            )}
            {error && (
              <div
                className={styles.submissionMessage}
                style={{ color: 'red' }}
              >
                {error}
              </div>
            )}
          </div>
        </form>
      </Page.Content>
    </Page.Container>
  );
}

InfluencerInitializer.propTypes = {
  currentUser: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    role: PropTypes.oneOf(Object.values(Role)),
  }).isRequired,
  sendForm: PropTypes.func.isRequired,
  getInfluencers: PropTypes.func.isRequired,
  influencers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default InfluencerInitializer;
